import type { Cache, UpdateResolver, UpdatesConfig, Variables } from '@urql/exchange-graphcache';
import { GetPreparedBasketDocument } from '../generated/shopping/GetPreparedBasketDocument';
import type {
  CheckoutOrderableBasketFragmentFragment,
  CheckoutTargetPage,
  GetPreparedBasketQuery,
  GetPreparedBasketQueryVariables,
} from '../generated/shopping/graphql';

/**
 * Cache semantics of the prepared (checkout) basket.
 *
 * `getPreparedBasket(page: X)` is a page-specific view of the basket: the backend computes
 * `targetPage` (and filters the messages) for the *requested* page. Two queries for different
 * pages therefore must not share one normalized entity, otherwise a `CHANGE_DELIVERY_PAGE` read
 * overwrites the `targetPage` of the cached `CHECK_AND_ORDER` read and the `/kasse` layout gate
 * bounces between the two pages. `CheckoutOrderableBasket` is thus deliberately *not* keyed (see
 * `cacheKeySettings`) and lives embedded under its own `Query.getPreparedBasket(args)` entry.
 *
 * Mutations returning the basket are the only way those entries change without a network round
 * trip. For each of them:
 *
 * - The returned basket is written into every cached entry of the response's `targetPage`, and
 *   that entry is seeded when the page has not been queried yet. The backend computes an update
 *   response for the page it sends the customer to (`convertBasketMessages(targetPage, …)`, the
 *   payment/shipment flags derive from the possible target page), so the navigation that follows
 *   a mutation is served from the cache instead of running the basket calculation a second time.
 * - The entries of the page the mutation was sent from receive the basket as well, but keep their
 *   own `targetPage`: the response's verdict describes the transition *out of* that page, and a
 *   later visit of the page must not replay it as "go elsewhere".
 * - Every other cached entry is invalidated, so a later visit of that page refetches instead of
 *   replaying a basket state from before the mutation.
 * - A `targetPage` that holds for every page (`NO_ORDER`, `LOGIN_PAGE`) is written into all
 *   cached entries so the active gate redirects without another request.
 * - A response without any page to target — or one carrying only a partial basket — invalidates
 *   every cached entry.
 *
 * A `CheckoutServerFailure` result leaves the cache untouched — the caller shows the failure and
 * the cached pages are still valid.
 */

const PREPARED_BASKET_FIELD = 'getPreparedBasket';

/** Redirect verdicts that hold for every checkout page, not only the requested one. */
const GLOBAL_TARGET_PAGES: ReadonlySet<CheckoutTargetPage> = new Set<CheckoutTargetPage>([
  'NO_ORDER',
  'LOGIN_PAGE',
]);

type OrderableBasketResult = Extract<
  GetPreparedBasketQuery['getPreparedBasket'],
  { __typename: 'CheckoutOrderableBasket' }
> &
  Pick<CheckoutOrderableBasketFragmentFragment, 'targetPage'>;

type CodeVariables = Pick<GetPreparedBasketQueryVariables, 'coupons' | 'loyaltyCoupons'>;

type CachePlan =
  /** The basket holds for every page: write it into every cached entry. */
  | { kind: 'everywhere' }
  /** Nothing can be updated reliably: drop every cached entry. */
  | { kind: 'invalidate' }
  | {
      kind: 'pages';
      /** Entries of these pages receive the basket as returned. */
      targetPages: ReadonlySet<CheckoutTargetPage>;
      /** Entries of this page receive the basket but keep their own `targetPage`. */
      originPage?: CheckoutTargetPage;
      /** Locale for seeding a target page that has not been queried yet. */
      locale?: string;
      /**
       * The mutation's own coupon arguments. The pages query with the same values next, because
       * both derive them from the durable code store, so that entry is seeded as well.
       */
      codeVariables?: CodeVariables;
    };

const isOrderableBasket = (value: unknown): value is OrderableBasketResult =>
  typeof value === 'object' &&
  value !== null &&
  '__typename' in value &&
  value.__typename === 'CheckoutOrderableBasket';

const stringArgument = (args: Variables | null | undefined, name: string): string | undefined => {
  const value = args?.[name];
  return typeof value === 'string' ? value : undefined;
};

/** The `page` argument of a cached `getPreparedBasket` entry or of the update mutation. */
const pageArgument = (args: Variables | null | undefined): CheckoutTargetPage | undefined => {
  const page = stringArgument(args, 'page');
  // Both operations declare `$page: CheckoutTargetPage!`, so a recorded string is one of its values.
  return page === undefined ? undefined : (page as CheckoutTargetPage);
};

/** True for the `{ locale, page }` entry the layout gate and the page containers query. */
const isPlainEntry = (args: Variables | null): boolean =>
  Object.keys(args ?? {}).every((name) => name === 'locale' || name === 'page');

const cachedTargetPage = (data: GetPreparedBasketQuery | null): CheckoutTargetPage | undefined => {
  const cached = data?.getPreparedBasket;
  return isOrderableBasket(cached) ? (cached.targetPage ?? undefined) : undefined;
};

const writeBasket = (
  cache: Cache,
  variables: GetPreparedBasketQueryVariables,
  basket: OrderableBasketResult,
  keepTargetPageOf?: CheckoutTargetPage,
): void => {
  cache.updateQuery<GetPreparedBasketQuery, GetPreparedBasketQueryVariables>(
    { query: GetPreparedBasketDocument, variables },
    (data) => ({
      getPreparedBasket:
        keepTargetPageOf === undefined
          ? basket
          : { ...basket, targetPage: cachedTargetPage(data) ?? keepTargetPageOf },
    }),
  );
};

const applyPlan = (cache: Cache, basket: OrderableBasketResult, plan: CachePlan): void => {
  const entries = cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === PREPARED_BASKET_FIELD);

  if (plan.kind !== 'pages') {
    for (const { arguments: args } of entries) {
      if (plan.kind === 'everywhere') {
        writeBasket(cache, args as GetPreparedBasketQueryVariables, basket);
      } else {
        cache.invalidate('Query', PREPARED_BASKET_FIELD, args);
      }
    }
    return;
  }

  const seededPages = new Set<CheckoutTargetPage>();
  for (const { arguments: args } of entries) {
    const page = pageArgument(args);
    // The arguments were recorded from this very query, so they are its variables.
    const variables = args as GetPreparedBasketQueryVariables;

    if (page !== undefined && plan.targetPages.has(page)) {
      writeBasket(cache, variables, basket);
      if (isPlainEntry(args)) {
        seededPages.add(page);
      }
    } else if (page !== undefined && page === plan.originPage) {
      writeBasket(cache, variables, basket, page);
    } else {
      cache.invalidate('Query', PREPARED_BASKET_FIELD, args);
    }
  }

  const locale =
    plan.locale ?? entries.map((entry) => stringArgument(entry.arguments, 'locale')).find(Boolean);
  if (locale === undefined) {
    return;
  }
  for (const page of plan.targetPages) {
    if (!seededPages.has(page)) {
      writeBasket(cache, { locale, page }, basket);
    }
  }

  if (plan.codeVariables === undefined) {
    return;
  }
  for (const page of plan.targetPages) {
    writeBasket(cache, { locale, page, ...plan.codeVariables }, basket);
  }
  if (plan.originPage !== undefined) {
    writeBasket(
      cache,
      { locale, page: plan.originPage, ...plan.codeVariables },
      basket,
      plan.originPage,
    );
  }
};

const toCodeVariables = (
  coupons: string | undefined,
  loyaltyCoupons: string | undefined,
): CodeVariables | undefined => {
  if (coupons === undefined && loyaltyCoupons === undefined) {
    return undefined;
  }
  // Only the arguments that were sent, so the key matches the query that omits the others.
  return {
    ...(coupons === undefined ? {} : { coupons }),
    ...(loyaltyCoupons === undefined ? {} : { loyaltyCoupons }),
  };
};

/** The coupon arguments of the update mutation, `undefined` when it sent none. */
const codeVariablesOf = (args: Variables | null | undefined): CodeVariables | undefined =>
  toCodeVariables(stringArgument(args, 'coupons'), stringArgument(args, 'loyaltyCoupons'));

const planFor = (
  basket: OrderableBasketResult,
  mutationPage: CheckoutTargetPage | undefined,
  locale: string | undefined,
): CachePlan => {
  const targetPage = basket.targetPage ?? undefined;
  if (targetPage !== undefined && GLOBAL_TARGET_PAGES.has(targetPage)) {
    return { kind: 'everywhere' };
  }
  if (targetPage === undefined && mutationPage === undefined) {
    return { kind: 'invalidate' };
  }

  return {
    kind: 'pages',
    targetPages: new Set(targetPage === undefined ? [] : [targetPage]),
    originPage: mutationPage !== targetPage ? mutationPage : undefined,
    locale,
  };
};

const updateFromPreparedBasketMutation: UpdateResolver = (result, args, cache) => {
  const basket = result.updatePreparedBasket;
  if (!isOrderableBasket(basket)) {
    return;
  }

  const plan = planFor(basket, pageArgument(args), stringArgument(args, 'locale'));
  applyPlan(
    cache,
    basket,
    plan.kind === 'pages' ? { ...plan, codeVariables: codeVariablesOf(args) } : plan,
  );
};

const orderableBasketOf = (value: unknown): unknown =>
  typeof value === 'object' && value !== null && 'orderableBasket' in value
    ? value.orderableBasket
    : undefined;

/**
 * Mutations answering with `CheckoutChangedOrderableBasket { orderableBasket }` have no `page`
 * argument and carry `locale` inside their `payload`; the backend answers them with the
 * `targetPage` of the page they are used on, and the locale is taken from the cached entries.
 */
const updateFromChangedOrderableBasket =
  (field: string): UpdateResolver =>
  (result, _args, cache) => {
    const basket = orderableBasketOf(result[field]);
    if (!isOrderableBasket(basket)) {
      return;
    }

    applyPlan(cache, basket, planFor(basket, undefined, undefined));
  };

/** The PAYBACK actions are offered on "Prüfen & Bestellen" only. */
const LOYALTY_ACTION_PAGE: CheckoutTargetPage = 'CHECK_AND_ORDER';

type LoyaltyProgram = CheckoutOrderableBasketFragmentFragment['loyaltyProgram'];

/** The codes the checkout keeps after a loyalty action: those the backend accepted. */
const acceptedLoyaltyCodesOf = (loyaltyProgram: LoyaltyProgram | undefined): string | undefined =>
  (loyaltyProgram?.promotionCodes ?? [])
    .flatMap(({ promotionCode, isValid }) =>
      promotionCode && isValid === true ? [promotionCode] : [],
    )
    .join(',') || undefined;

const loyaltyActionPayloadOf = (args: Variables): Variables | null =>
  (args.payload ?? null) as Variables | null;

/**
 * The PAYBACK actions answer with an `orderableBasket` whose `targetPage` may be empty, which the
 * generic updater would treat as "invalidate everything". They are written into their page
 * instead, and additionally under the codes the page queries with next.
 */
const writeLoyaltyResult = (
  cache: Cache,
  basket: OrderableBasketResult,
  codeVariables: CodeVariables | undefined,
): void => {
  const plan = planFor(basket, LOYALTY_ACTION_PAGE, undefined);
  applyPlan(cache, basket, plan.kind === 'pages' ? { ...plan, codeVariables } : plan);
};

/** Adding the number keeps the conflict choice and the codes, so its own payload is that key. */
const updateFromAddLoyaltyCard: UpdateResolver = (result, args, cache) => {
  const basket = orderableBasketOf(result.addLoyaltyCard);
  if (!isOrderableBasket(basket)) {
    return;
  }

  writeLoyaltyResult(cache, basket, codeVariablesOf(loyaltyActionPayloadOf(args)));
};

/**
 * Every coupon action and the removal of the number drop the conflict choice, and the store keeps
 * only the accepted codes (the same rule `usePaybackBox` stores by).
 */
const updateFromLoyaltyCodeChange =
  (field: string): UpdateResolver =>
  (result, args, cache) => {
    const basket = orderableBasketOf(result[field]);
    if (!isOrderableBasket(basket)) {
      return;
    }

    const { loyaltyProgram } = basket as OrderableBasketResult &
      Pick<CheckoutOrderableBasketFragmentFragment, 'loyaltyProgram'>;
    writeLoyaltyResult(
      cache,
      basket,
      toCodeVariables(
        stringArgument(loyaltyActionPayloadOf(args), 'coupons'),
        acceptedLoyaltyCodesOf(loyaltyProgram),
      ),
    );
  };

/**
 * Mutations that return only a partial `orderableBasket` (e.g. the SEPA mandate flow selects
 * `allowedPayments` alone) cannot fill a cached entry; every cached page is dropped instead so the
 * next read refetches the changed basket.
 */
const invalidateAfterPartialBasket =
  (field: string): UpdateResolver =>
  (result, _args, cache) => {
    const basket = orderableBasketOf(result[field]);
    if (basket === undefined) {
      return;
    }

    for (const { arguments: args } of cache.inspectFields('Query')) {
      cache.invalidate('Query', PREPARED_BASKET_FIELD, args);
    }
  };

export const preparedBasketCacheUpdates: UpdatesConfig['Mutation'] = {
  updatePreparedBasket: updateFromPreparedBasketMutation,
  toggleServiceInOrderableBasket: updateFromChangedOrderableBasket(
    'toggleServiceInOrderableBasket',
  ),
  setServiceCountInOrderableBasket: updateFromChangedOrderableBasket(
    'setServiceCountInOrderableBasket',
  ),
  deleteProductFromOrderableBasket: updateFromChangedOrderableBasket(
    'deleteProductFromOrderableBasket',
  ),
  changeProductInOrderableBasket: updateFromChangedOrderableBasket(
    'changeProductInOrderableBasket',
  ),
  addLoyaltyCard: updateFromAddLoyaltyCard,
  removeLoyaltyCard: updateFromLoyaltyCodeChange('removeLoyaltyCard'),
  addLoyaltyCoupon: updateFromLoyaltyCodeChange('addLoyaltyCoupon'),
  removeLoyaltyCoupon: updateFromLoyaltyCodeChange('removeLoyaltyCoupon'),
  setServicePriceInOrderableBasket: updateFromChangedOrderableBasket(
    'setServicePriceInOrderableBasket',
  ),
  grantSepaDirectDebitAuthorisation: invalidateAfterPartialBasket(
    'grantSepaDirectDebitAuthorisation',
  ),
  revokeSepaDirectDebitAuthorisation: invalidateAfterPartialBasket(
    'revokeSepaDirectDebitAuthorisation',
  ),
};
